/******************************************************************************
 *** SysMbrasAprvrMng ** 조합원 결재자 관리
 *******************************************************************************/
import React from "react";
import AppContext from "@/js/common/AppContext";
import Axios from "@/js/common/Axios";
import Button from "@/js/common/ui/Button";
import Util from "@/js/common/Util";
import SelectBox from "@/js/common/ui/input/SelectBox";
import Modal from "@/js/common/ui/modal/Modal";
import DialogTitleBar from "@/js/system/common/DialogTitleBar";
import MbrasAprvrAuthMbrPickDialog from "./dialog/MbrasAprvrAuthMbrPickDialog";
import { useRecoilValue } from "recoil";
import * as Atom from "@/js/common/Atom";

const UNION_HEAD_OFFICE_CD = "00_000";

const SysMbrasAprvrMng = () => { // 조합원 결재자 관리 화면
    const userInfo = useRecoilValue(Atom.userInfo);

    const [upOgnzCd            , setUpOgnzCd            ] = React.useState<string>(userInfo.upOgnzCd);         // 상위조직코드(본부)
    const [ognzCd              , setOgnzCd              ] = React.useState<string>(`${userInfo.upOgnzCd}_000`);// 조직코드
    const [callTime            , setCallTime            ] = React.useState<Date>(new Date());                  // 조회 호출 시간
    const [dataArr             , setDataArr             ] = React.useState<any[]>([]);                         // 결재자 목록
    const [memberPickOpen      , setMemberPickOpen      ] = React.useState<boolean>(false);                    // 담당자 선택 팝업 여부
    const [pickMode            , setPickMode            ] = React.useState<"add" | "edit">("edit");            // 선택 모드
    const [pickTargetAtrzStngId, setPickTargetAtrzStngId] = React.useState<string>("");                        // 수정 대상 결재설정 ID

    const isSystemAdmin = userInfo.systemAdmin === "Y";
    const isUnionHeadOffice = userInfo.ognzCd === UNION_HEAD_OFFICE_CD;
    const isUnionMemberAdmin =
        isUnionHeadOffice && userInfo.memberAdmin === "Y";
    const isLocalHeadOffice = userInfo.ognzCd === `${userInfo.upOgnzCd}_000`;
    const isBranchOffice = !isUnionHeadOffice && !isLocalHeadOffice;

    // 시스템관리자 또는 조합본부의 조합원관리자만
    // 본부 필터 및 추가/수정/삭제 기능 사용 가능
    const canManageAprvrLine = isSystemAdmin || isUnionMemberAdmin;

    const canEditRow = (row: any) => { // 행 수정 가능 여부 확인
        return canManageAprvrLine && String(row.orgCd || "") === String(ognzCd || "");
    };
        
    React.useEffect(() => { // 결재자 목록 조회
        if (!ognzCd) {
            setDataArr([]);
            return;
        }

        AppContext.showSpinner();
        Axios.dataAccess({
            url: "system/sys/aprvr/select-mbras-aprvr-list.do",
            methodType: "post",
            paramType: "json",
            paramData: {
                orgCd: ognzCd,
                atrzSeCd: "03",
            },
            onSuccessFn: (res: any) => {
                setDataArr(res.result.list);
                AppContext.hideSpinner();
            },
        });
    }, [callTime, ognzCd]);

    const openNoAuthAlert = () => { // 권한 안내창 표시
        AppContext.openAlert({
            title: "알림",
            msg: "다른 조직의 결재라인은 조회만 가능합니다.",
            icon: "info",
            closeText: "확인",
        });
    };

    const saveAll = (rows: any[], successMsg: string) => { // 결재자 목록 저장
        if (!canManageAprvrLine) {
            openNoAuthAlert();
            return;
        }

        const dataList = rows
            .filter((row: any) => String(row.orgCd || "") === String(ognzCd || ""))
            .map((row: any, idx: number) => ({
                orgCd: ognzCd,
                atrzSeCd: "03",
                atrzStpSeq: idx + 1,
                picUserId: String(row.picUserId || "").trim(),
                useYn: row.useYn || "Y",
            }));

        const hasMissingUserId = dataList.some((item: any) => !item.picUserId);
        if (hasMissingUserId) {
            AppContext.openAlert({
                title: "알림",
                msg: "담당자ID는 필수입니다.",
                icon: "info",
                closeText: "확인",
            });
            return;
        }

        const userIdList = dataList.map((item: any) => item.picUserId);
        const hasDuplicateUserId = new Set(userIdList).size !== userIdList.length;
        if (hasDuplicateUserId) {
            AppContext.openAlert({
                title: "알림",
                msg: "결재자에 중복된 담당자ID가 있습니다. 중복을 제거해주세요.",
                icon: "warning",
                closeText: "확인",
            });
            return;
        }

        AppContext.showSpinner();
        Axios.dataAccess({
            url: "system/sys/aprvr/save-aprvr.do",
            methodType: "post",
            paramType: "json",
            paramData: { orgCd: ognzCd, atrzSeCd: "03", dataList },
            onSuccessFn: () => {
                AppContext.hideSpinner();
                AppContext.openAlert({
                    title: "알림",
                    msg: successMsg,
                    icon: "info",
                    closeText: "확인",
                });

                setCallTime(new Date());
                setDataArr(rows);
            },
        });
    };

    const onAdd = () => { // 결재자 추가
        if (!canManageAprvrLine) {
            openNoAuthAlert();
            return;
        }

        if (!ognzCd) {
            AppContext.openAlert({
                title: "알림",
                msg: "조직을 먼저 선택해주세요.",
                icon: "info",
                closeText: "확인",
            });
            return;
        }

        setPickMode("add");
        setPickTargetAtrzStngId("");
        setMemberPickOpen(true);
    };

    const openMemberPickForEdit = (row: any) => { // 결재자 수정창 열기
        if (!canEditRow(row)) {
            openNoAuthAlert();
            return;
        }

        setPickMode("edit");
        setPickTargetAtrzStngId(String(row.atrzStngId));
        setMemberPickOpen(true);
    };

    const closeMemberPick = () => { // 담당자 선택창 닫기
        setMemberPickOpen(false);
        setPickTargetAtrzStngId("");
    };

    const onPickMember = (item: any) => { // 선택한 담당자 반영
        if (!canManageAprvrLine) {
            closeMemberPick();
            openNoAuthAlert();
            return;
        }

        const pickedUserId = String(item?.userId || "").trim();
        const pickedUserNm = String(item?.userNm || "").trim();
        const pickedJbpsSeNm = String(item?.jbpsSeNm || "").trim();

        if (!pickedUserId) {
            closeMemberPick();
            return;
        }

        if (pickMode === "add") {
            const nextRows = dataArr.concat({
                atrzStngId: `NEW_${Date.now()}_${Math.random().toString(16).slice(2)}`,
                orgCd: ognzCd,
                atrzSeCd: "03",
                picUserId: pickedUserId,
                picUserNm: pickedUserNm,
                jbpsSeNm: pickedJbpsSeNm,
                useYn: "Y",
            });

            closeMemberPick();
            saveAll(nextRows, "추가되었습니다.");
            return;
        }

        const nextRows = dataArr.map((row: any) =>
            String(row.atrzStngId) === String(pickTargetAtrzStngId)
                ? {
                        ...row,
                        picUserId: pickedUserId,
                        picUserNm: pickedUserNm,
                        jbpsSeNm: pickedJbpsSeNm,
                    }
                : row
        );

        closeMemberPick();
        saveAll(nextRows, "수정되었습니다.");
    };

    const onDeleteRow = (row: any, idx: number) => { // 결재자 삭제
        if (!canEditRow(row)) {
            openNoAuthAlert();
            return;
        }

        const stepText = `${idx + 1}차 결재자`;

        const nmText =
            row.picUserNm && row.picUserId
                ? `${row.picUserNm}(${row.picUserId})`
                : row.picUserId
                ? `(${row.picUserId})`
                : "";

        AppContext.openAlert({
            title: "확인",
            msg: `${stepText} ${nmText ? `- ${nmText} ` : ""}행을 삭제하시겠습니까?`,
            icon: "question",
            closeText: "취소",
            confirmText: "삭제",
            handleConfirm: () => {
                const atrzStngId = String(row.atrzStngId || "");
                const isNew = atrzStngId.startsWith("NEW_");

                if (isNew) {
                    const nextRows = dataArr.filter((item: any) => String(item.atrzStngId) !== atrzStngId);
                    saveAll(nextRows, "삭제되었습니다.");
                    return;
                }

                AppContext.showSpinner();
                Axios.dataAccess({
                    url: "system/sys/aprvr/delete-aprvr.do",
                    methodType: "post",
                    paramType: "json",
                    paramData: { atrzStngId },
                    onSuccessFn: () => {
                        AppContext.hideSpinner();
                        const nextRows = dataArr.filter((item: any) => String(item.atrzStngId) !== atrzStngId);
                        saveAll(nextRows, "삭제되었습니다.");
                    },
                });
            },
        });
    };

    const selectedUserIds = React.useMemo(() => { // 선택된 사용자 ID 목록
        return dataArr
            .map((row: any) => String(row.picUserId || "").trim())
            .filter((userId: string) => userId !== "");
    }, [dataArr]);

    const currentUserId = React.useMemo(() => { // 현재 수정 대상 사용자 ID
        if (pickMode !== "edit") return "";
        const target = dataArr.find((row: any) => String(row.atrzStngId) === String(pickTargetAtrzStngId));
        return String(target?.picUserId || "").trim();
    }, [dataArr, pickMode, pickTargetAtrzStngId]);

    return (
        <div className="w1200 bgcWhite p20 bdrd8 mt10 boxShadowSoft">
            <div className="mt5">
                <span className="dpBlock w100p fm-c fs25 fc2c3e50 ls1 pb10">
                    {canManageAprvrLine ? "조합원 결재자 관리" : "조합원 결재자 조회"}
                </span>
                <div className="bdt3 bdc007bff pb10"></div>
            </div>

            <div className="bd1 bdcDDDDDD p20 mb10 bdrd6 bgcFAFAFA">
                <div className="dpFlx aiC gap14 jcSb">
                    <div className="dpFlx aiC gap14">
                        <div className="dpFlx aiC">
                            <span className="w40 fm-c fs15 fc333333 ls1 taL">본부</span>
                            <SelectBox
                                value={upOgnzCd}
                                width={150}
                                height={36}
                                fontSize={14}
                                fontFamily="fm-c"
                                color="#333"
                                letterSpacing={-1}
                                borderColor="#ddd"
                                borderOnColor="#222"
                                borderRadius={2}
                                background={canManageAprvrLine ? "#FFFFFF" : "#F6F6F6"}
                                codeArray={Util.getUpOrgCodeArray("01")}
                                placeholder="전체"
                                paddingRight={8}
                                disabled={!canManageAprvrLine}
                                onChangeFunc={(data: string) => {
                                    setUpOgnzCd(data);
                                    setOgnzCd(String(data || "").endsWith("_000") ? data : `${data}_000`);
                                }}
                            />
                        </div>

                        {isBranchOffice && (
                            <div className="dpFlx aiC">
                                <span className="w50 fm-c fs15 fc333333 ls1 taL">지부</span>
                                <SelectBox
                                    value={userInfo.ognzCd}
                                    width={150}
                                    height={36}
                                    fontSize={14}
                                    fontFamily="fm-c"
                                    color="#333"
                                    letterSpacing={-1}
                                    borderColor="#ddd"
                                    borderOnColor="#222"
                                    borderRadius={2}
                                    background="#F6F6F6"
                                    codeArray={Util.getDownOrgCodeArray(userInfo.upOgnzCd)}
                                    placeholder="전체"
                                    paddingRight={8}
                                    disabled={true}
                                    onChangeFunc={() => {}}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="dpFlx aiC mb10 h44">
                <div className="fm-b fs16 fc007bff ls1 fw700">
                    <span>결재자 목록</span>
                </div>

                <div className="flx1 taC">
                    {!canManageAprvrLine && (
                        <span className="fc007bff fs14 fm-c">
                            결재라인은 시스템관리자 또는 조합본부의 조합원관리자만 수정할 수 있습니다.
                        </span>
                    )}
                </div>

                <Button
                    width={100}
                    height={40}
                    fontFamily="fm-b"
                    fontSize={16}
                    letterSpacing={-1}
                    variant="styCompleteBtn"
                    borderRadius={6}
                    buttonClass="dpFlx aiC jcC"
                    buttonName="추가"
                    disabled={!canManageAprvrLine}
                    onClickFunc={onAdd}
                />
            </div>

            <div className="w100p bsBdb bgcFDFCFC bdt1 bdcC5C5C5">
                <table className="w100p tlF tbl-collapse">
                    <colgroup>
                        <col style={{ width: "15%" }} />
                        <col style={{ width: "25%" }} />
                        <col style={{ width: "15%" }} />
                        <col style={{ width: "25%" }} />
                        <col style={{ width: "10%" }} />
                        <col style={{ width: "10%" }} />
                    </colgroup>

                    <thead>
                        <tr>
                            {["결재 단계", "결재 역할 (직책)", "소속기관", "결재자 이름(ID)", "수정", "삭제"].map((label) => (
                                <th key={label} className="p0 bd1 bdcEDEDED">
                                    <div className="h40 dpFlx aiC jcC pw12 bgcF1F8FF fm-Pretendard-Medium fw400 fs14 fc34495e ls1 wsNw ofH toE">
                                        {label}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {dataArr.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="p0 bd1 bdcEDEDED">
                                    <div className="ph25 taC fc999999 fm-Pretendard-Regular fs13 ls075">
                                        조회된 데이터가 없습니다.
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            dataArr.map((row: any, idx: number) => {
                                const stageText = `${idx + 1}차 결재자`;
                                const assigneeText = row.picUserNm
                                    ? `${row.picUserNm} (${row.picUserId})`
                                    : row.picUserId
                                    ? `(${row.picUserId})`
                                    : "";

                                return (
                                    <tr key={row.atrzStngId || idx} className="bgcF9F9F9-hover transitionBgc">
                                        <td className="p0 bd1 bdcEDEDED">
                                            <div className="h35 dpFlx aiC pw12 fm-Pretendard-Light fs13 fc333333 ls075 wsNw ofH toE">
                                                {stageText}
                                            </div>
                                        </td>

                                        <td className="p0 bd1 bdcEDEDED">
                                            <div className="h35 dpFlx aiC pw12">
                                                <div className="flx1 h30 dpFlx aiC fm-c fs13 fc333333 ls-1 wsNw ofH toE pl10">
                                                    {row.jbpsSeNm}
                                                </div>
                                            </div>
                                        </td>

                                        <td className="p0 bd1 bdcEDEDED">
                                            <div className="h35 dpFlx aiC pw12">
                                                <div className="flx1 h30 dpFlx aiC fm-c fs13 fc333333 ls-1 wsNw ofH toE pl10">
                                                    {row.ognzNm}
                                                </div>
                                            </div>
                                        </td>

                                        <td className="p0 bd1 bdcEDEDED">
                                            <div className="h35 dpFlx aiC gap8 pw12">
                                                <div
                                                    className="flx1"
                                                    style={{
                                                        fontSize: 13,
                                                        fontFamily: "fm-c",
                                                        color: "#333",
                                                        letterSpacing: -1,
                                                    }}
                                                >
                                                    {assigneeText}
                                                </div>
                                            </div>
                                        </td>

                                        <td className="p0 bd1 bdcEDEDED">
                                            <div className="h35 dpFlx aiC jcC">
                                                {canEditRow(row) && (
                                                    <Button
                                                        width={70}
                                                        height={30}
                                                        fontFamily="fm-b"
                                                        fontSize={13}
                                                        letterSpacing={-1}
                                                        variant="styTempBtn"
                                                        borderRadius={6}
                                                        buttonClass="dpFlx aiC jcC"
                                                        buttonName="수정"
                                                        onClickFunc={() => openMemberPickForEdit(row)}
                                                    />
                                                )}    
                                            </div>
                                        </td>

                                        <td className="p0 bd1 bdcEDEDED">
                                            <div className="h35 dpFlx aiC jcC">
                                                {canEditRow(row) && (
                                                    <Button
                                                        width={70}
                                                        height={30}
                                                        fontFamily="fm-b"
                                                        fontSize={13}
                                                        letterSpacing={-1}
                                                        variant="styDeleteBtn"
                                                        borderRadius={6}
                                                        buttonClass="dpFlx aiC jcC"
                                                        buttonName="삭제"
                                                        onClickFunc={() => onDeleteRow(row, idx)}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            <Modal
                modalOpen={memberPickOpen}
                dim={true}
                visible={true}
                titleEl={<DialogTitleBar title={"담당자(회원) 선택"} />}
                onCloseFunc={closeMemberPick}
                width={800}
            >
                <div className="h900">
                    <MbrasAprvrAuthMbrPickDialog
                        onPick={onPickMember}
                        setModalOpen={setMemberPickOpen}
                        initUpOgnzCd={upOgnzCd}
                        selectedUserIds={selectedUserIds}
                        currentUserId={pickMode === "edit" ? currentUserId : ""}
                    />
                </div>
            </Modal>
        </div>
    );
};

export default SysMbrasAprvrMng;